var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import { combineReducers } from 'redux';
import { flatten } from 'flat';
import { createSelector, createSelectorCreator, defaultMemoize } from 'reselect';
import { getLocalizedElement, getIndexForLanguageCode, objectValuesToString, validateOptions } from './utils';

/**
 * TYPES
 */


/**
 * ACTIONS
 */
export var INITIALIZE = '@@localize/INITIALIZE';
export var ADD_TRANSLATION = '@@localize/ADD_TRANSLATION';
export var ADD_TRANSLATION_FOR_LANGUGE = '@@localize/ADD_TRANSLATION_FOR_LANGUGE';
export var SET_LANGUAGES = '@@localize/SET_LANGUAGES';
export var SET_ACTIVE_LANGUAGE = '@@localize/SET_ACTIVE_LANGUAGE';
export var TRANSLATE = '@@localize/TRANSLATE';

/**
 * REDUCERS
 */
export function languages() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  var action = arguments[1];

  switch (action.type) {
    case INITIALIZE:
    case SET_LANGUAGES:
      var _options = action.payload.options || {};
      var _activeLanguage = action.payload.activeLanguage || _options.defaultLanguage;
      return action.payload.languages.map(function (language, index) {
        var isActive = function isActive(code) {
          return _activeLanguage !== undefined ? code === _activeLanguage : index === 0;
        };
        // check if it's using array of Language objects, or array of languge codes
        return typeof language === 'string' ? { code: language, active: isActive(language) // language codes
        } : _extends({}, language, { active: isActive(language.code) }); // language objects
      });
    case SET_ACTIVE_LANGUAGE:
      return state.map(function (language) {
        return language.code === action.payload.languageCode ? _extends({}, language, { active: true }) : _extends({}, language, { active: false });
      });
    default:
      return state;
  }
}

export function translations() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments[1];

  switch (action.type) {
    case ADD_TRANSLATION:
      // apply transformation if set in options
      var _translations = action.translationTransform !== undefined ? action.translationTransform(action.payload.translation, action.languageCodes) : action.payload.translation;
      return _extends({}, state, flatten(_translations, { safe: true }));
    case ADD_TRANSLATION_FOR_LANGUGE:
      var languageIndex = action.languageCodes.indexOf(action.payload.language);
      var flattenedTranslations = languageIndex >= 0 ? flatten(action.payload.translation) : {};
      // convert single translation data into multiple
      var languageTranslations = Object.keys(flattenedTranslations).reduce(function (prev, cur) {
        // loop through each language, and for languages that don't match active language 
        // keep existing translation data, and for active language store new translation data
        var translationValues = action.languageCodes.map(function (code, index) {
          var existingValues = state[cur] || [];
          return index === languageIndex ? flattenedTranslations[cur] : existingValues[index];
        });
        return _extends({}, prev, _defineProperty({}, cur, translationValues));
      }, {});

      return _extends({}, state, languageTranslations);
    default:
      return state;
  }
}

export function options() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : defaultTranslateOptions;
  var action = arguments[1];

  switch (action.type) {
    case INITIALIZE:
      var _options2 = action.payload.options || {};
      return _extends({}, state, validateOptions(_options2));
    default:
      return state;
  }
};

export var defaultTranslateOptions = {
  renderInnerHtml: true,
  showMissingTranslationMsg: true
};

var initialState = {
  languages: [],
  translations: {},
  options: defaultTranslateOptions
};

export var localeReducer = function localeReducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : initialState;
  var action = arguments[1];

  var languageCodes = state.languages.map(function (language) {
    return language.code;
  });
  var translationTransform = state.options.translationTransform;
  return {
    languages: languages(state.languages, action),
    translations: translations(state.translations, _extends({}, action, { languageCodes: languageCodes, translationTransform: translationTransform })),
    options: options(state.options, action)
  };
};

/**
 * ACTION CREATORS
 */
export var initialize = function initialize(languages) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultTranslateOptions;
  return {
    type: INITIALIZE,
    payload: { languages: languages, options: options }
  };
};

export var addTranslation = function addTranslation(translation) {
  return {
    type: ADD_TRANSLATION,
    payload: { translation: translation }
  };
};

export var addTranslationForLanguage = function addTranslationForLanguage(translation, language) {
  return {
    type: ADD_TRANSLATION_FOR_LANGUGE,
    payload: { translation: translation, language: language }
  };
};

export var setLanguages = function setLanguages(languages, activeLanguage) {
  return {
    type: SET_LANGUAGES,
    payload: { languages: languages, activeLanguage: activeLanguage }
  };
};

export var setActiveLanguage = function setActiveLanguage(languageCode) {
  return {
    type: SET_ACTIVE_LANGUAGE,
    payload: { languageCode: languageCode }
  };
};

/**
 * SELECTORS
 */
export var getTranslations = function getTranslations(state) {
  return state.translations;
};

export var getLanguages = function getLanguages(state) {
  return state.languages;
};

export var getOptions = function getOptions(state) {
  return state.options;
};

export var getActiveLanguage = function getActiveLanguage(state) {
  var languages = getLanguages(state);
  return languages.filter(function (language) {
    return language.active === true;
  })[0];
};

export var translationsEqualSelector = createSelectorCreator(defaultMemoize, function (cur, prev) {
  var isTranslationsData = !(Array.isArray(cur) || Object.keys(cur).toString() === 'code,active');

  // for translations data use a combination of keys and values for comparison
  if (isTranslationsData) {
    var prevTranslations = prev;
    var curTranslations = cur;

    var prevKeys = Object.keys(prevTranslations).toString();
    var curKeys = Object.keys(curTranslations).toString();

    var prevValues = objectValuesToString(prevTranslations);
    var curValues = objectValuesToString(curTranslations);

    var prevCacheValue = prevKeys + ' - ' + prevValues;
    var curCacheValue = curKeys + ' - ' + curValues;

    return prevCacheValue === curCacheValue;
  }

  return prev === cur;
});

export var getTranslationsForActiveLanguage = translationsEqualSelector(getActiveLanguage, getLanguages, getTranslations, function (activeLanguage, languages, translations) {
  var activeLanguageCode = activeLanguage.code;

  var activeLanguageIndex = getIndexForLanguageCode(activeLanguageCode, languages);
  return Object.keys(translations).reduce(function (prev, key) {
    return _extends({}, prev, _defineProperty({}, key, translations[key][activeLanguageIndex]));
  }, {});
});

export var getTranslate = createSelector(getTranslationsForActiveLanguage, getActiveLanguage, getOptions, function (translations, activeLanguage, options) {
  return function (value) {
    var data = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var optionsOverride = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};

    var translateOptions = _extends({}, options, optionsOverride);
    if (typeof value === 'string') {
      return getLocalizedElement(value, translations, data, activeLanguage, translateOptions);
    } else if (Array.isArray(value)) {
      return value.reduce(function (prev, cur) {
        return _extends({}, prev, _defineProperty({}, cur, getLocalizedElement(cur, translations, data, activeLanguage, translateOptions)));
      }, {});
    } else {
      throw new Error('react-localize-redux: Invalid key passed to getTranslate.');
    }
  };
});


//////////////////
// WEBPACK FOOTER
// ../~/react-localize-redux/es/locale.js
// module id = 263
// module chunks = 0